type RetryPolicy = {
  attempts: number
  baseDelayMs: number
  backoffFactor: number
  maxDelayMs: number
  jitterRatio: number
}

type StallPolicy = {
  /** Abort if no bytes are received for this long. */
  stallTimeoutMs: number
  /** Optional: overall per-attempt cap (kept large; stallTimeoutMs is primary). */
  attemptTimeoutMs: number
}

export type PlayerNetTelemetry = {
  stage: 'manifest' | 'segment'
  url: string
  attempt: number
  attempts: number
  status: number | null
  timed_out: boolean
  stall_timeout_ms: number
  attempt_timeout_ms: number
  duration_ms: number
  stall_ms: number
  bytes_received: number
  content_length: number | null
  kbps: number | null
  range_used: boolean
  resumed: boolean
  reason: 'stall' | 'attempt_timeout' | 'abort' | 'http' | 'network' | 'decode'
}

function nowMs(): number {
  return Date.now()
}

function sleep(ms: number): Promise<void> {
  const t = Math.max(0, Number(ms || 0))
  return new Promise((r) => setTimeout(r, t))
}

function clamp(n: number, a: number, b: number): number {
  return Math.min(b, Math.max(a, n))
}

function jitteredDelay(baseMs: number, jitterRatio: number): number {
  const j = clamp(Number(jitterRatio || 0), 0, 1)
  if (j <= 0) return Math.max(0, Math.floor(baseMs))
  const lo = baseMs * (1 - j)
  const hi = baseMs * (1 + j)
  return Math.max(0, Math.floor(lo + Math.random() * (hi - lo)))
}

function defaultPublicInit(init?: RequestInit): RequestInit {
  return { credentials: 'omit', ...(init || {}) }
}

function parseContentLength(res: Response): number | null {
  try {
    const v = String(res.headers?.get?.('content-length') || '').trim()
    if (!v) return null
    const n = Number(v)
    return Number.isFinite(n) && n > 0 ? n : null
  } catch {
    return null
  }
}

async function readAllBytesWithStallAbort(
  res: Response,
  ctrl: AbortController | null,
  stallTimeoutMs: number,
  attemptTimeoutMs: number,
  opts?: { onProgress?: (bytesReceived: number) => void },
): Promise<{ bytes: Uint8Array; bytesReceived: number; stallMs: number; durationMs: number }> {
  const startedAt = nowMs()
  let bytesReceived = 0
  let stallMs = 0
  let abortReason: 'stall' | 'attempt_timeout' | 'abort' | null = null

  const body: any = (res as any).body
  const hasReader = !!body?.getReader
  if (!hasReader) {
    const t = setTimeout(() => { try { ctrl?.abort(); } catch {} }, Math.max(500, attemptTimeoutMs))
    try {
      const ab = await res.arrayBuffer()
      bytesReceived = ab?.byteLength || 0
      try { opts?.onProgress?.(bytesReceived) } catch {}
      return { bytes: new Uint8Array(ab), bytesReceived, stallMs: 0, durationMs: Math.max(0, nowMs() - startedAt) }
    } finally {
      try { clearTimeout(t) } catch {}
    }
  }

  const reader = body.getReader()
  const chunks: Uint8Array[] = []
  let lastProgressAt = nowMs()
  let lastTickAt = lastProgressAt

  const stallT = setInterval(() => {
    const now = nowMs()
    const gap = now - lastProgressAt
    const dt = now - lastTickAt
    lastTickAt = now
    // Only count time as "stall" if there was no progress for at least one tick.
    if (gap >= 250) stallMs += dt
    if (gap >= stallTimeoutMs) {
      abortReason = abortReason || 'stall'
      try { ctrl?.abort() } catch {}
    }
  }, 250)

  const hardT = setTimeout(() => {
    abortReason = abortReason || 'attempt_timeout'
    try { ctrl?.abort() } catch {}
  }, Math.max(500, attemptTimeoutMs))

  try {
    while (true) {
      let r: any
      try {
        r = await reader.read()
      } catch (e: any) {
        // Preserve partial progress for the caller (used for Range resume).
        try {
          const merged = new Uint8Array(bytesReceived)
          let off = 0
          for (const c of chunks) { merged.set(c, off); off += c.byteLength }
          ;(e as any).partial_bytes = merged
          ;(e as any).bytes_received = bytesReceived
          ;(e as any).stall_ms = stallMs
          ;(e as any).duration_ms = Math.max(0, nowMs() - startedAt)
          ;(e as any).abort_reason = abortReason || 'abort'
        } catch {}
        throw e
      }
      if (r?.done) break
      const u8 = r?.value ? (r.value instanceof Uint8Array ? r.value : new Uint8Array(r.value)) : new Uint8Array()
      if (u8.byteLength) {
        chunks.push(u8)
        bytesReceived += u8.byteLength
        lastProgressAt = nowMs()
        try { opts?.onProgress?.(bytesReceived) } catch {}
      }
    }
  } finally {
    try { clearInterval(stallT) } catch {}
    try { clearTimeout(hardT) } catch {}
  }

  const merged = new Uint8Array(bytesReceived)
  let off = 0
  for (const c of chunks) {
    merged.set(c, off)
    off += c.byteLength
  }
  return { bytes: merged, bytesReceived, stallMs: Math.max(0, stallMs), durationMs: Math.max(0, nowMs() - startedAt) }
}

export async function fetchMseManifestJson(
  url: string,
  opts: {
    signal?: AbortSignal
    retry?: Partial<RetryPolicy>
    stall?: Partial<StallPolicy>
    onRecovered?: (meta: { from: PlayerNetTelemetry; success_attempt: number; attempts: number; total_duration_ms: number }) => void
  } = {},
): Promise<{ json: any }> {
  const retry: RetryPolicy = {
    attempts: Math.max(1, Number(opts.retry?.attempts ?? 3)),
    baseDelayMs: Math.max(0, Number(opts.retry?.baseDelayMs ?? 250)),
    backoffFactor: Math.max(1, Number(opts.retry?.backoffFactor ?? 2)),
    maxDelayMs: Math.max(0, Number(opts.retry?.maxDelayMs ?? 4000)),
    jitterRatio: clamp(Number(opts.retry?.jitterRatio ?? 0.2), 0, 1),
  }
  const stall: StallPolicy = {
    stallTimeoutMs: Math.max(500, Number(opts.stall?.stallTimeoutMs ?? 3500)),
    attemptTimeoutMs: Math.max(1000, Number(opts.stall?.attemptTimeoutMs ?? 20000)),
  }

  let lastErr: any = null
  let lastRetryable: PlayerNetTelemetry | null = null
  for (let attempt = 1; attempt <= retry.attempts; attempt += 1) {
    const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null
    const startedAt = nowMs()
    let abortedByExternal = false
    const onAbort = () => { abortedByExternal = true; try { ctrl?.abort() } catch {} }
    try {
      try { opts.signal?.addEventListener?.('abort', onAbort, { once: true } as any) } catch {}
      const res = await fetch(url, defaultPublicInit({ cache: 'no-cache' as any, signal: (ctrl as any)?.signal }))
      const status = Number((res as any)?.status || 0) || 0
      if (!res.ok) {
        // Retry on 5xx and transient 0/400-ish (WebView weirdness)
        const shouldRetry = attempt < retry.attempts && (status >= 500 || status === 0 || status === 400)
        const e: any = new Error(`Manifest HTTP ${status}: ${url}`)
        e.telemetry = {
          stage: 'manifest',
          url,
          attempt,
          attempts: retry.attempts,
          status: status || null,
          timed_out: false,
          stall_timeout_ms: stall.stallTimeoutMs,
          attempt_timeout_ms: stall.attemptTimeoutMs,
          duration_ms: Math.max(0, nowMs() - startedAt),
          stall_ms: 0,
          bytes_received: 0,
          content_length: parseContentLength(res),
          kbps: null,
          range_used: false,
          resumed: false,
          reason: 'http',
        } satisfies PlayerNetTelemetry
        lastErr = e
        lastRetryable = (e as any)?.telemetry && typeof (e as any).telemetry === 'object' ? (e as any).telemetry : lastRetryable
        if (shouldRetry) {
          const d = Math.min(retry.maxDelayMs, retry.baseDelayMs * Math.pow(retry.backoffFactor, attempt - 1))
          await sleep(jitteredDelay(d, retry.jitterRatio))
          continue
        }
        throw e
      }

      const bytes = await readAllBytesWithStallAbort(res, ctrl, stall.stallTimeoutMs, stall.attemptTimeoutMs)
      const txt = new TextDecoder('utf-8').decode(bytes.bytes)
      const json = txt ? JSON.parse(txt) : null
      if (lastRetryable && attempt > 1) {
        try { opts.onRecovered?.({ from: lastRetryable, success_attempt: attempt, attempts: retry.attempts, total_duration_ms: Math.max(0, nowMs() - startedAt) }) } catch {}
      }
      return { json }
    } catch (e: any) {
      const name = String(e?.name || '').toLowerCase()
      const isAbort = name.includes('abort')
      const timedOut = isAbort && !abortedByExternal

      const tele: PlayerNetTelemetry = {
        stage: 'manifest',
        url,
        attempt,
        attempts: retry.attempts,
        status: (e as any)?.telemetry?.status ?? null,
        timed_out: timedOut,
        stall_timeout_ms: stall.stallTimeoutMs,
        attempt_timeout_ms: stall.attemptTimeoutMs,
        duration_ms: Math.max(0, nowMs() - startedAt),
        stall_ms: Number((e as any)?.stall_ms ?? 0) || 0,
        bytes_received: Number((e as any)?.bytes_received ?? 0) || 0,
        content_length: (e as any)?.telemetry?.content_length ?? null,
        kbps: null,
        range_used: false,
        resumed: false,
        reason: isAbort
          ? (abortedByExternal ? 'abort' : String((e as any)?.abort_reason || 'stall') === 'attempt_timeout' ? 'attempt_timeout' : 'stall')
          : 'network',
      }
      if (typeof (e as any)?.telemetry === 'object') {
        ;(e as any).telemetry = { ...(e as any).telemetry, ...tele }
      } else {
        ;(e as any).telemetry = tele
      }

      lastErr = e
      if (attempt < retry.attempts && !abortedByExternal) {
        lastRetryable = (e as any)?.telemetry && typeof (e as any).telemetry === 'object' ? (e as any).telemetry : lastRetryable
        const d = Math.min(retry.maxDelayMs, retry.baseDelayMs * Math.pow(retry.backoffFactor, attempt - 1))
        await sleep(jitteredDelay(d, retry.jitterRatio))
        continue
      }
      throw e
    } finally {
      try { opts.signal?.removeEventListener?.('abort', onAbort as any) } catch {}
    }
  }
  throw lastErr || new Error(`Manifest fetch failed: ${url}`)
}

export async function fetchMseSegmentArrayBuffer(
  url: string,
  opts: {
    signal?: AbortSignal
    retry?: Partial<RetryPolicy>
    stall?: Partial<StallPolicy>
    resumeRange?: boolean
    onRecovered?: (meta: { from: PlayerNetTelemetry; success_attempt: number; attempts: number; total_duration_ms: number; resumed: boolean; bytes_received: number }) => void
  } = {},
): Promise<ArrayBuffer> {
  const retry: RetryPolicy = {
    attempts: Math.max(1, Number(opts.retry?.attempts ?? 4)),
    baseDelayMs: Math.max(0, Number(opts.retry?.baseDelayMs ?? 250)),
    backoffFactor: Math.max(1, Number(opts.retry?.backoffFactor ?? 2)),
    maxDelayMs: Math.max(0, Number(opts.retry?.maxDelayMs ?? 5000)),
    jitterRatio: clamp(Number(opts.retry?.jitterRatio ?? 0.25), 0, 1),
  }
  const stall: StallPolicy = {
    stallTimeoutMs: Math.max(800, Number(opts.stall?.stallTimeoutMs ?? 5000)),
    attemptTimeoutMs: Math.max(2000, Number(opts.stall?.attemptTimeoutMs ?? 45000)),
  }

  const allowResume = opts.resumeRange === true
  let lastErr: any = null
  let lastRetryable: PlayerNetTelemetry | null = null
  let carried: Uint8Array | null = null
  let carriedBytes = 0

  for (let attempt = 1; attempt <= retry.attempts; attempt += 1) {
    const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null
    const startedAt = nowMs()
    let abortedByExternal = false
    const onAbort = () => { abortedByExternal = true; try { ctrl?.abort() } catch {} }
    try {
      try { opts.signal?.addEventListener?.('abort', onAbort, { once: true } as any) } catch {}

      const headers: any = {}
      const useRange = allowResume && carriedBytes > 0
      if (useRange) headers.Range = `bytes=${carriedBytes}-`

      const res = await fetch(url, defaultPublicInit({ headers, signal: (ctrl as any)?.signal }))
      const status = Number((res as any)?.status || 0) || 0

      if (useRange && status === 200) {
        // Server ignored Range; discard partial and restart clean.
        carried = null
        carriedBytes = 0
      }

      if (!res.ok) {
        const shouldRetry = attempt < retry.attempts && (status >= 500 || status === 0 || status === 400)
        const e: any = new Error(`Segment HTTP ${status}: ${url}`)
        e.telemetry = {
          stage: 'segment',
          url,
          attempt,
          attempts: retry.attempts,
          status: status || null,
          timed_out: false,
          stall_timeout_ms: stall.stallTimeoutMs,
          attempt_timeout_ms: stall.attemptTimeoutMs,
          duration_ms: Math.max(0, nowMs() - startedAt),
          stall_ms: 0,
          bytes_received: carriedBytes,
          content_length: parseContentLength(res),
          kbps: null,
          range_used: useRange,
          resumed: carriedBytes > 0,
          reason: 'http',
        } satisfies PlayerNetTelemetry
        lastErr = e
        lastRetryable = (e as any)?.telemetry && typeof (e as any).telemetry === 'object' ? (e as any).telemetry : lastRetryable
        if (shouldRetry) {
          const d = Math.min(retry.maxDelayMs, retry.baseDelayMs * Math.pow(retry.backoffFactor, attempt - 1))
          await sleep(jitteredDelay(d, retry.jitterRatio))
          continue
        }
        throw e
      }

      const out = await readAllBytesWithStallAbort(res, ctrl, stall.stallTimeoutMs, stall.attemptTimeoutMs)

      // Merge carried + new bytes
      const total = carriedBytes + out.bytesReceived
      const merged = new Uint8Array(total)
      if (carried && carriedBytes > 0) merged.set(carried, 0)
      merged.set(out.bytes, carriedBytes)

      // success
      if (lastRetryable && attempt > 1) {
        try {
          opts.onRecovered?.({
            from: lastRetryable,
            success_attempt: attempt,
            attempts: retry.attempts,
            total_duration_ms: Math.max(0, nowMs() - startedAt),
            resumed: carriedBytes > 0,
            bytes_received: merged.byteLength,
          })
        } catch {}
      }
      return merged.buffer
    } catch (e: any) {
      const name = String(e?.name || '').toLowerCase()
      const isAbort = name.includes('abort')
      const timedOut = isAbort && !abortedByExternal

      // Update carried bytes on mid-stream abort (best-effort) to enable Range resume.
      if (allowResume && isAbort && !abortedByExternal) {
        const part: Uint8Array | null = (e as any)?.partial_bytes instanceof Uint8Array ? (e as any).partial_bytes : null
        const partBytes = Number((e as any)?.bytes_received ?? (part ? part.byteLength : 0)) || 0
        if (part && partBytes > 0) {
          // If we already had bytes from previous attempts, append the new partial.
          if (carried && carriedBytes > 0) {
            const next = new Uint8Array(carriedBytes + partBytes)
            next.set(carried, 0)
            next.set(part, carriedBytes)
            carried = next
            carriedBytes = next.byteLength
          } else {
            carried = part
            carriedBytes = Math.max(0, partBytes)
          }
        }
      }

      // If we aborted mid-stream and resume is enabled, keep partial.
      // Note: we only have carried bytes from previous attempts; current attempt bytes are only preserved
      // if we got a Response and reader progressed; in WebViews, abort may happen at any stage.
      const tele: PlayerNetTelemetry = {
        stage: 'segment',
        url,
        attempt,
        attempts: retry.attempts,
        status: (e as any)?.telemetry?.status ?? null,
        timed_out: timedOut,
        stall_timeout_ms: stall.stallTimeoutMs,
        attempt_timeout_ms: stall.attemptTimeoutMs,
        duration_ms: Math.max(0, nowMs() - startedAt),
        stall_ms: Number((e as any)?.stall_ms ?? 0) || 0,
        bytes_received: Number((e as any)?.bytes_received ?? carriedBytes) || carriedBytes,
        content_length: (e as any)?.telemetry?.content_length ?? null,
        kbps: null,
        range_used: allowResume && carriedBytes > 0,
        resumed: carriedBytes > 0,
        reason: isAbort
          ? (abortedByExternal ? 'abort' : String((e as any)?.abort_reason || 'stall') === 'attempt_timeout' ? 'attempt_timeout' : 'stall')
          : 'network',
      }
      if (typeof (e as any)?.telemetry === 'object') {
        ;(e as any).telemetry = { ...(e as any).telemetry, ...tele }
      } else {
        ;(e as any).telemetry = tele
      }

      lastErr = e
      if (attempt < retry.attempts && !abortedByExternal) {
        lastRetryable = (e as any)?.telemetry && typeof (e as any).telemetry === 'object' ? (e as any).telemetry : lastRetryable
        // We can only "resume" across attempts if we already accumulated something.
        // Current attempt partial is not guaranteed to be preserved across abort; keep it simple and safe:
        // only resume if we already had carried bytes.
        const d = Math.min(retry.maxDelayMs, retry.baseDelayMs * Math.pow(retry.backoffFactor, attempt - 1))
        await sleep(jitteredDelay(d, retry.jitterRatio))
        continue
      }
      throw e
    } finally {
      try { opts.signal?.removeEventListener?.('abort', onAbort as any) } catch {}
    }
  }

  throw lastErr || new Error(`Segment fetch failed: ${url}`)
}

